import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Appointment, AppointmentPaymentStatus, AppointmentStatus } from '../models/appointment.model';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { AppointmentIdKey } from '../config/constants';
import { WindowService } from './window.service';
import { ErrorService } from './error.service';
import { AppointmentApiService } from './appointment-api.service';
import { Router } from '@angular/router';
import { LocationService } from './location.service';

@Injectable({ providedIn: 'root' })
export class AppointmentStore {
  private readonly appointment$ = new BehaviorSubject<Appointment | null>(null);

  constructor(
    private windowService: WindowService,
    private errorService: ErrorService,
    private appointmentApiService: AppointmentApiService,
    private router: Router,
    private locationService: LocationService,
  ) {}

  setAppointment(appointment: Appointment) {
    this.appointment$.next(appointment);
    this.windowService.setStorage(AppointmentIdKey(appointment.locationId), appointment.id.toString());
  }

  setAppointmentId(locationId: number, appointmentId: number) {
    this.windowService.setStorage(AppointmentIdKey(locationId), appointmentId.toString());
  }

  watchAppointment(): Observable<Appointment | null> {
    return this.appointment$.asObservable();
  }

  clearAppointment(locationId: number) {
    this.windowService.removeStorage(AppointmentIdKey(locationId));
    this.appointment$.next(null);
  }

  appointmentIsEmpty(): Observable<boolean> {
    return this.watchAppointment().pipe(
      map((appointment) => appointment?.appointmentItems && appointment.appointmentItems.length === 0),
    );
  }

  async initAppointment(
    locationId: number,
    isTestMode: boolean,
    utmSource: string,
    utmMedium: string,
    utmCampaign: string,
    utmContent: string,
  ): Promise<Appointment> {
    if (isTestMode) {
      this.clearAppointment(locationId);
      return this.createNewBlankAndNavigate(locationId, true, utmSource, utmMedium, utmCampaign, utmContent);
    }

    const currentAppointment = await this.getCurrentAppointment(locationId);
    if (!currentAppointment) {
      return this.createNewBlankAndNavigate(locationId, isTestMode, utmSource, utmMedium, utmCampaign, utmContent);
    } else {
      const offlineAppointemnWithDeposit =
        currentAppointment.utmSource === 'in-salon' &&
        currentAppointment.status === AppointmentStatus.Booked &&
        currentAppointment.paymentStatus === AppointmentPaymentStatus.PartialPaymentRequired;
      if (currentAppointment.status !== AppointmentStatus.Cart && !offlineAppointemnWithDeposit) {
        // this appointment has gone past the checkout process, so we need to create a new one
        this.clearAppointment(locationId);
        return this.createNewBlankAndNavigate(locationId, isTestMode, utmSource, utmMedium, utmCampaign, utmContent);
      } else {
        this.setAppointment(currentAppointment);
        return currentAppointment;
      }
    }
  }

  async getCurrentAppointment(locationId: number): Promise<Appointment | null> {
    // first, try to get it from the store
    const appointment = await firstValueFrom(this.watchAppointment());
    // if it's missing in the store, try to get it from the local storage
    const appointmentId = appointment?.id || +this.windowService.getStorage(AppointmentIdKey(locationId));

    if (appointmentId) {
      // get the fresh details from the API
      try {
        return await firstValueFrom(this.appointmentApiService.getOne(appointmentId));
      } catch (error) {
        const errorMsg = this.errorService.extractApiError(error);
        if (error instanceof HttpErrorResponse && error.status === 404) {
          console.warn('Error fetching appointment:', errorMsg, 'Creating a new appointment');
          this.clearAppointment(locationId);
          return null;
        }

        throw error;
      }
    }

    // no current appointment found
    return null;
  }

  private async createNewBlankAndNavigate(
    locationId: number,
    isTestMode: boolean,
    utmSource: string,
    utmMedium: string,
    utmCampaign: string,
    utmContent: string,
  ): Promise<Appointment> {
    const appointment = await this.createNewBlank(
      locationId,
      isTestMode,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
    );
    this.navigateToFirstStep();
    return appointment;
  }

  private async createNewBlank(
    locationId: number,
    isTestMode: boolean,
    utmSource: string,
    utmMedium: string,
    utmCampaign: string,
    utmContent: string,
  ): Promise<Appointment> {
    const newAppointment: Partial<Appointment> = {
      locationId,
      test: isTestMode,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
    };
    const appointment = await firstValueFrom(this.appointmentApiService.createOne(newAppointment));
    if (!appointment.appointmentItems) {
      appointment.appointmentItems = [];
    }
    this.setAppointment(appointment);
    this.windowService.setStorage(AppointmentIdKey(locationId), appointment.id.toString());
    return appointment;
  }

  navigateToFirstStep() {
    const location = this.locationService.getLocation();
    if (location) {
      void this.router.navigate([location.uuid, 'services']);
    }
  }
}
